// src/components/jobDetails/RunInfoTab.jsx
import { useState, useEffect } from 'react';
import API from '../../api/api';
import { toast } from 'react-toastify';
import { ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

export default function RunInfoTab({ 
  currentRun, 
  jobId,
  wellIfo,
  shouldEnableSurveyButtons, 
  hasCalculated 
}) {
  const [activeSection, setActiveSection] = useState('details');
  const [uploadedFiles, setUploadedFiles] = useState({ inrun: null, outrun: null });
  const [operationResults, setOperationResults] = useState({
    qualityCheck: null,
    surveyCalculation: null,
    comparison: null
  });
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [loadingQualityCheck, setLoadingQualityCheck] = useState(false);
  const [loadingSurvey, setLoadingSurvey] = useState(false);
  const [activeOperation, setActiveOperation] = useState(null);
  const [isInterpolated, setIsInterpolated] = useState(false);
  const [showInterpolationPrompt, setShowInterpolationPrompt] = useState(false);
  const [interpolationStep, setInterpolationStep] = useState('choice');
  const [interpolationParams, setInterpolationParams] = useState({
    resolution: '',
    fromDepth: '',
    toDepth: '',
    fullSurvey: false
  });
  const [processing, setProcessing] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState({ inrun: null, outrun: null });
  const [showUploadModal, setShowUploadModal] = useState(false);

  // Reset state when currentRun changes
  useEffect(() => {
    if (currentRun) {
      // Reset all state related to the previous run
      setUploadedFiles({ inrun: null, outrun: null });
      setOperationResults({
        qualityCheck: null,
        surveyCalculation: null,
        comparison: null
      });
      setActiveOperation(null);
      setIsInterpolated(false);
      setShowInterpolationPrompt(false);
      setInterpolationStep('choice');
      setInterpolationParams({
        resolution: '',
        fromDepth: '',
        toDepth: '',
        fullSurvey: false
      });
      setSelectedFiles({ inrun: null, outrun: null });
      setShowUploadModal(false);
      
      // Check if files were already uploaded for this run
      checkExistingFiles();
      checkExistingQualityCheck();
      checkExistingSurveyCalculation();
    }
  }, [currentRun]);

  const checkExistingQualityCheck = async () => {
    if (!currentRun?.id) return;
    
    try {
      const response = await API.get(`/api/upload-quality-check/${jobId}/${currentRun.id}/`);
      if (response.data) {
        setOperationResults(prev => ({ ...prev, qualityCheck: response.data }));
      }
    } catch (error) {
      console.error('Error checking quality check results:', error);
      // It's okay if no quality check results exist yet
    }
  };

  const checkExistingSurveyCalculation = async () => {
    if (!currentRun?.id) return;
    
    try {
      // First check for interpolated data
      const interpolatedResponse = await API.get(`/survey-interpolate/${currentRun.id}/`);
      if (interpolatedResponse?.data?.data?.length > 0) {
        setOperationResults(prev => ({ 
          ...prev, 
          surveyCalculation: interpolatedResponse.data,
          isInterpolated: true
        }));
        return;
      }
    } catch (interpolatedError) {
      console.log('No interpolated data found');
    }
    
    try {
      // Then check for calculated survey
      const calculatedResponse = await API.get(`/runs/calculate-survey/${currentRun.id}/`);
      if (calculatedResponse?.data?.details?.length > 0) {
        setOperationResults(prev => ({ 
          ...prev, 
          surveyCalculation: calculatedResponse.data,
          isInterpolated: false
        }));
      }
    } catch (calculatedError) {
      console.log('No calculated survey found');
    }
  };

  const checkExistingFiles = async () => {
    if (!currentRun?.id) return;
    setLoadingFiles(true);

    try {
      const response = await API.get(`/api/upload-status/${currentRun.id}/`);
      if (response.data.inrun_uploaded || response.data.outrun_uploaded) {
        setUploadedFiles({
          inrun: response.data.inrun_uploaded ? { name: response.data.inrun_file || "Inrun File" } : null,
          outrun: response.data.outrun_uploaded ? { name: response.data.outrun_file || "Outrun File" } : null
        });
      } else {
        setUploadedFiles({ inrun: null, outrun: null });
      }
    } catch (error) {
      console.error('Error checking upload status:', error);
      setUploadedFiles({ inrun: null, outrun: null });
    } finally {
      setLoadingFiles(false);
    }
  };

  const formatValue = (value, unit = '') => {
    if (value === null || value === undefined || value === '') return "-";
    return `${value}${unit}`;
  };

  const sections = [
    { id: 'details', label: 'Run Details', icon: '📋' },
    { id: 'upload', label: 'Upload Data', icon: '📤' },
    { id: 'operations', label: 'Operations', icon: '🔄' },
  ];

  const handleFileSelect = (e, fileType) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFiles(prev => ({ ...prev, [fileType]: file }));
    }
  };

  const handleUploadSubmit = async () => {
    if (!currentRun?.id) return;
    
    try {
      setProcessing(true);
      const formData = new FormData();
      
      // Get survey type from currentRun
      const surveyTypeId = currentRun.survey_type;
      
      // Handle different survey types
      if (surveyTypeId === 1 || surveyTypeId === 2) {
        // Single file upload for survey types 1 and 2
        if (!selectedFiles.inrun) {
          toast.error('Please upload an Excel file');
          return;
        }
        formData.append('file', selectedFiles.inrun);
      } else {
        // Dual file upload for other survey types
        if (!selectedFiles.inrun || !selectedFiles.outrun) {
          toast.error('Please upload both Excel files');
          return;
        }
        formData.append('inrun_file', selectedFiles.inrun);
        formData.append('outrun_file', selectedFiles.outrun);
      }
      
      // Add run_id to form data
      formData.append('run_id', currentRun.id);
      
      // Use the correct endpoint - try both possibilities if needed
      let response;
      try {
        // First try the quality check upload endpoint
        response = await API.post(`/api/upload-quality-check/${jobId}/${currentRun.id}/`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
      } catch (error) {
        // If that fails, try the survey upload endpoint
        console.log('Trying alternative upload endpoint...');
        response = await API.post(`/api/upload-survey/${jobId}/${currentRun.id}/`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
      }
      
      if (response.status === 201 || response.status === 200) {
        toast.success('Files uploaded successfully!');
        setUploadedFiles(selectedFiles);
        setShowUploadModal(false);
        setSelectedFiles({ inrun: null, outrun: null });
        
        // Refresh the quality check data
        checkExistingQualityCheck();
        checkExistingSurveyCalculation();
        
        // Automatically switch to operations tab after successful upload
        setActiveSection('operations');
      }
    } catch (error) {
      console.error('Upload error:', error);
      if (error.response?.data?.error) {
        toast.error(error.response.data.error);
      } else {
        toast.error('Failed to upload files. Please check your connection and try again.');
      }
    } finally {
      setProcessing(false);
    }
  };

  const handleQualityCheck = async () => {
    try {
      setProcessing(true);
      const response = await API.get(`/api/upload-quality-check/${jobId}/${currentRun.id}/`);
      setOperationResults(prev => ({ ...prev, qualityCheck: response.data }));
      setActiveOperation('qualityCheck');
      toast.success('Quality check completed');
    } catch (error) {
      console.error('Quality check error:', error);
      toast.error('Failed to perform quality check');
    } finally {
      setProcessing(false);
    }
  };

  const handleSurveyCalculation = async () => {
    try {
      try {
        const interpolatedResponse = await API.get(`/survey-interpolate/${currentRun.id}/`);
        if (interpolatedResponse?.data?.data?.length > 0) {
          setOperationResults(prev => ({ 
            ...prev, 
            surveyCalculation: interpolatedResponse.data,
            isInterpolated: true
          }));
          setActiveOperation('surveyCalculation');
          toast.success('Showing interpolated survey');
          return;
        }
      } catch (interpolatedError) {
        console.log('No interpolated data found');
      }

      // Check for calculated survey
      try {
        const calculatedResponse = await API.get(`/runs/calculate-survey/${currentRun.id}/`);
        if (calculatedResponse?.data?.details?.length > 0) {
          setOperationResults(prev => ({ 
            ...prev, 
            surveyCalculation: calculatedResponse.data,
            isInterpolated: false
          }));
          setActiveOperation('surveyCalculation');
          toast.success('Showing calculated survey');
          return;
        }
      } catch (calculatedError) {
        console.log('No calculated survey found');
      }

      // If neither exists, show interpolation prompt
      setShowInterpolationPrompt(true);
      setInterpolationStep('choice');
      
    } catch (err) {
      console.error(err);
      toast.error('Failed to check survey data');
    }
  };

  const handleInterpolationYes = async () => {
    setProcessing(true);
    try {
      // First, calculate the original survey
      await API.post(`/runs/calculate-survey/${currentRun.id}/`);
      toast.success('Original survey calculated successfully');
      // Then show interpolation inputs
      setInterpolationStep('inputs');
    } catch (error) {
      console.error(error);
      toast.error('Failed to calculate original survey');
    } finally {
      setProcessing(false);
    }
  };

  const handleInterpolationSubmit = async () => {
    const { resolution, fromDepth, toDepth, fullSurvey } = interpolationParams;
    
    if (!resolution) {
      toast.error('Please enter resolution value');
      return;
    }

    // Validate depth range only if not doing full survey
    if (!fullSurvey) {
      if (!fromDepth || !toDepth) {
        toast.error('Please enter both From and To depths');
        return;
      }
      if (Number(fromDepth) >= Number(toDepth)) {
        toast.error('"From Depth" must be less than "To Depth"');
        return;
      }
    }

    setProcessing(true);
    try {
      const response = await API.post(`/survey-interpolate/${currentRun.id}/`, {
        resolution: Number(resolution),
        survey_from: fullSurvey ? null : Number(fromDepth),
        survey_to: fullSurvey ? null : Number(toDepth),
        full_survey: fullSurvey
      });

      if (response.status === 201 && response.data?.data) {
        toast.success(`Survey ${fullSurvey ? 'fully' : 'partially'} interpolated`);
        setOperationResults(prev => ({ 
          ...prev, 
          surveyCalculation: response.data,
          isInterpolated: true
        }));
        setActiveOperation('surveyCalculation');
        setShowInterpolationPrompt(false);
      } else {
        throw new Error(response.data?.message || 'Interpolation failed');
      }
    } catch (error) {
      console.error('Interpolation error:', error);
      toast.error(error.message);
    } finally {
      setProcessing(false);
      setInterpolationParams({
        resolution: '',
        fromDepth: '',
        toDepth: '',
        fullSurvey: false
      });
    }
  };

  const handleInterpolationNo = async () => {
    setProcessing(true);
    try {
      const response = await API.post(`/runs/calculate-survey/${currentRun.id}/`);
      setOperationResults(prev => ({ 
        ...prev, 
        surveyCalculation: response.data,
        isInterpolated: false
      }));
      setActiveOperation('surveyCalculation');
      toast.success('Survey calculation completed');
    } catch (error) {
      console.error(error);
      toast.error('Survey calculation failed');
    } finally {
      setProcessing(false);
      setShowInterpolationPrompt(false);
    }
  };

  const handleComparison = async () => {
    try {
      setProcessing(true);
      // Implementation for comparison would go here
      toast.info('Comparison feature to be implemented');
      // setOperationResults(prev => ({ ...prev, comparison: response.data }));
      // setActiveOperation('comparison');
    } catch (error) {
      console.error('Comparison error:', error);
      toast.error('Failed to perform comparison');
    } finally {
      setProcessing(false);
    }
  };

  const getField = (row, field) => {
    if (operationResults.isInterpolated) {
      const fieldMap = {
        latitude: 'ns',
        departure: 'ew',
        true_vertical_depth: 'tvd',
        Vertical_Section: 'vertical_section',
        DLS: 'dls',
        CL: 'cl'
      };
      return row[fieldMap[field] || field];
    }
    return row[field];
  };

  const renderQualityCheckResults = () => {
    if (!operationResults.qualityCheck) return null;
    
    const data = operationResults.qualityCheck;
    
    const getStatusColor = (status) => {
      switch (status?.toLowerCase()) {
        case 'high': return 'bg-green-600 text-white';
        case 'good': return 'bg-green-400 text-gray-800';
        case 'low': return 'bg-green-200 text-gray-800';
        case 'pass': return 'bg-green-600 text-white';
        default: return '';
      }
    };

    return (
      <div className="mt-6 bg-white p-4 rounded-lg border border-gray-200">
        <h4 className="text-lg font-semibold mb-4">Quality Check Results</h4>
        
        <div className="mb-4 flex flex-wrap justify-between items-start gap-4 text-sm">
          <div className="space-y-1">
            <p className="text-xs sm:text-sm">
              <strong>G(t) Score:</strong> {data.summary?.g_t_score} ({data.summary?.g_t_percentage})
            </p>
            <p className="text-xs sm:text-sm">
              <strong>W(t) Score:</strong> {data.summary?.w_t_score} ({data.summary?.w_t_percentage})
            </p>
          </div>
          <div className="space-y-1 text-right">
            <p className="text-xs sm:text-sm">
              <strong>Uploaded File Name:</strong> {data.summary?.file_name}
            </p>
            <p className="text-xs sm:text-sm">
              <strong>Success Count:</strong> {data.summary?.success_count}
            </p>
          </div>
        </div>

        <div className="overflow-x-auto max-h-96 border border-gray-300 rounded-lg">
          <table className="min-w-full text-xs sm:text-sm">
            <thead className="sticky top-0 bg-gray-100 z-10">
              <tr>
                <th className="p-2 border w-20">Station</th>
                <th className="p-2 border">Depth</th>
                <th className="p-2 border">INC</th>
                <th className="p-2 border">AZG</th>
                <th className="p-2 border">G(t)</th>
                <th className="p-2 border">ΔTGF</th>
                <th className="p-2 border">ΔTGF Comp</th>
                <th className="p-2 border">W(t)</th>
                <th className="p-2 border">ΔW(t)</th>
                <th className="p-2 border">ΔW(t) Comp</th>
                <th className="p-2 border">Status</th>
              </tr>
            </thead>
            <tbody>
              {data.results?.map((row, idx) => (
                <tr key={idx} className="hover:bg-gray-50">
                  <td className="p-2 border">{idx + 1}</td>
                  <td className="p-2 border">{row.depth}</td>
                  <td className="p-2 border">{row.inc}</td>
                  <td className="p-2 border">{row.azg}</td>
                  <td className="p-2 border">{row.gt}</td>
                  <td className="p-2 border">{row.g_t_difference}</td>
                  <td className={`p-2 border ${getStatusColor(row.g_t_status)}`}>
                    {row.g_t_status}
                  </td>
                  <td className="p-2 border">{row.wt}</td>
                  <td className="p-2 border">{row.w_t_difference}</td>
                  <td className={`p-2 border ${getStatusColor(row.w_t_status)}`}>
                    {row.w_t_status}
                  </td>
                  <td className={`p-2 border ${getStatusColor(row.status)}`}>
                    {row.status}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderSurveyCalculationResults = () => {
    if (!operationResults.surveyCalculation) return null;
    
    // Handle different response structures
    const data = operationResults.surveyCalculation.details || 
                operationResults.surveyCalculation.data || 
                [];
    
    return (
      <div className="mt-6 bg-white p-4 rounded-lg border border-gray-200">
        <h4 className="text-lg font-semibold mb-4">
          {operationResults.isInterpolated ? 'Interpolated Survey Results' : 'Survey Calculation Results'}
        </h4>
        
        {data.length === 0 ? (
          <p className="text-gray-500">No survey data available.</p>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_3fr] gap-4">
            {/* Graphs section */}
            <div className="flex flex-col gap-4">
              {/* Graph 1: +N/-S vs +E/-W */}
              <div className="bg-white border border-gray-300 rounded-lg p-2 shadow">
                <h5 className="font-bold mb-2 text-gray-700">+N/-S vs +E/-W</h5>
                <ResponsiveContainer width="100%" height={250}>
                  <ScatterChart>
                    <CartesianGrid />
                    <XAxis
                      dataKey={operationResults.isInterpolated ? "ns" : "latitude"}
                      name="+N/-S"
                      unit="m"
                    />
                    <YAxis
                      dataKey={operationResults.isInterpolated ? "ew" : "departure"}
                      name="+E/-W"
                      unit="m"
                    />
                    <Tooltip cursor={{ strokeDasharray: '3 3' }} />
                    <Legend />
                    <Scatter data={data} fill="#ef4444" />
                  </ScatterChart>
                </ResponsiveContainer>
              </div>

              {/* Graph 2: Vertical Section vs TVD */}
              <div className="bg-white border border-gray-300 rounded-lg p-2 shadow">
                <h5 className="font-bold mb-2 text-gray-700">Vertical Section vs TVD</h5>
                <ResponsiveContainer width="100%" height={250}>
                  <ScatterChart>
                    <CartesianGrid />
                    <XAxis
                      dataKey={operationResults.isInterpolated ? "tvd" : "true_vertical_depth"}
                      name="TVD"
                      unit="m"
                    />
                    <YAxis
                      dataKey={operationResults.isInterpolated ? "vertical_section" : "Vertical_Section"}
                      name="Vertical Section"
                      unit="m"
                    />
                    <Tooltip cursor={{ strokeDasharray: '3 3' }} />
                    <Legend />
                    <Scatter data={data} fill="#3b82f6" />
                  </ScatterChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Table section */}
            <div className="overflow-x-auto  h-[545px] border border-gray-300 rounded-lg">
              <table className="w-full text-xs sm:text-sm">
                <thead className="sticky top-0 bg-gray-100 z-10">
                  <tr>
                    <th className="p-2 border">#</th>
                    <th className="p-2 border">Depth</th>
                    <th className="p-2 border">Inclination</th>
                    <th className="p-2 border">Azimuth</th>
                    <th className="p-2 border">+N/-S</th>
                    <th className="p-2 border">+E/-W</th>
                    <th className="p-2 border">TVD</th>
                    <th className="p-2 border">Vertical Section</th>
                    <th className="p-2 border">Closure Distance</th>
                    <th className="p-2 border">Closure Direction</th>
                    <th className="p-2 border">DLS</th>
                    {operationResults.isInterpolated && <th className="p-2 border">Original</th>}
                  </tr>
                </thead>
                <tbody>
                  {data.map((row, idx) => (
                    <tr key={idx} className="hover:bg-gray-50">
                      <td className="p-2 border">{idx + 1}</td>
                      <td className="p-2 border">{row.depth}</td>
                      <td className="p-2 border">{row.inclination}</td>
                      <td className="p-2 border">{row.azimuth}</td>
                      <td className="p-2 border">{getField(row, 'latitude')}</td>
                      <td className="p-2 border">{getField(row, 'departure')}</td>
                      <td className="p-2 border">{getField(row, 'true_vertical_depth')}</td>
                      <td className="p-2 border">{getField(row, 'Vertical_Section')}</td>
                      <td className="p-2 border">{row.closure_distance}</td>
                      <td className="p-2 border">{row.closure_direction}</td>
                      <td className="p-2 border">{getField(row, 'DLS')}</td>
                      {operationResults.isInterpolated && (
                        <td className="p-2 border">{row.is_original ? 'Yes' : 'No'}</td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderComparisonResults = () => {
    if (!operationResults.comparison) return null;
    
    return (
      <div className="mt-6 bg-white p-4 rounded-lg border border-gray-200">
        <h4 className="text-lg font-semibold mb-4">Comparison Results</h4>
        <p className="text-gray-600">Comparison feature to be implemented</p>
      </div>
    );
  };

  const renderUploadSection = () => {
    return (
      <div className="space-y-6">
        <div className="bg-gray-50 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Upload Survey Data</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            
            {/* Left side: Upload box OR Uploaded files */}
            <div className="text-center">
              {uploadedFiles.inrun || uploadedFiles.outrun ? (
                <div className="bg-white p-6 rounded-lg border border-green-300">
                  <div className="text-4xl mb-4">✅</div>
                  <h4 className="font-semibold text-gray-800 mb-2">File Uploaded for this Run</h4>
                  <ul className="text-sm text-gray-700 space-y-1">
                    {uploadedFiles.inrun && <li>Inrun: {uploadedFiles.inrun.name}</li>}
                    {uploadedFiles.outrun && <li>Outrun: {uploadedFiles.outrun.name}</li>}
                  </ul>
                </div>
              ) : (
                <div className="bg-white p-6 rounded-lg border-2 border-dashed border-gray-300 hover:border-red-400 transition-colors">
                  <div className="text-4xl mb-4">📊</div>
                  <h4 className="font-semibold text-gray-800 mb-2">Upload Survey Files</h4>
                  <p className="text-sm text-gray-600 mb-4">
                    Upload Excel files for quality check and processing
                  </p>
                  <button
                    onClick={() => setShowUploadModal(true)}
                    className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                  >
                    Upload Files
                  </button>
                </div>
              )}
            </div>

            {/* Right side: Upload requirements + status */}
            <div className="space-y-4">
              <div className="bg-white p-4 rounded-lg border border-gray-200">
                <h4 className="font-semibold text-gray-800 mb-2">Upload Requirements</h4>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Excel format (.xlsx, .xls)</li>
                  <li>• Supported survey types: Inrun, Outrun, Both</li>
                  <li>• Data should include depth, inclination, azimuth</li>
                  <li>• File size limit: 10MB</li>
                </ul>
              </div>

              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-semibold text-blue-800 mb-2">Current Status</h4>
                <div className="text-sm text-blue-700">
                  {(uploadedFiles.inrun || uploadedFiles.outrun) ? (
                    <div>
                      ✅ File uploaded for this run:
                      <ul className="list-disc list-inside mt-2 text-green-700">
                        {uploadedFiles.inrun && <li>Inrun: {uploadedFiles.inrun.name}</li>}
                        {uploadedFiles.outrun && <li>Outrun: {uploadedFiles.outrun.name}</li>}
                      </ul>
                    </div>
                  ) : (
                    '📝 No data uploaded yet'
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Upload History */}
          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <h4 className="font-semibold text-gray-800 mb-3">Upload History</h4>
            <div className="text-sm text-gray-600">
              {(uploadedFiles.inrun || uploadedFiles.outrun) ? (
                <div className="flex flex-col gap-1 text-green-600">
                  {uploadedFiles.inrun && <span>✅ Last Inrun upload: {uploadedFiles.inrun.name}</span>}
                  {uploadedFiles.outrun && <span>✅ Last Outrun upload: {uploadedFiles.outrun.name}</span>}
                </div>
              ) : (
                <div className="text-gray-500">No upload history available</div>
              )}
            </div>
          </div>
        </div>

        {/* Upload Modal */}
        {showUploadModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white p-6 rounded-lg shadow-lg max-w-md w-full mx-4">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Upload Files</h3>
              
              <div className="mb-4">
                {currentRun.survey_type && (
                  <p className="text-sm text-gray-600 mb-2">
                    Survey Type: {currentRun.survey_type_name || 'Unknown'}
                  </p>
                )}
                
                {currentRun.survey_type ? (
                  (currentRun.survey_type === 1 || currentRun.survey_type === 2) ? (
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Upload Excel File
                      </label>
                      <input 
                        type="file" 
                        accept=".xlsx, .xls"
                        onChange={(e) => handleFileSelect(e, 'inrun')}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      />
                      {selectedFiles.inrun && (
                        <p className="text-xs text-green-600 mt-1">
                          {selectedFiles.inrun.name} selected
                        </p>
                      )}
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Upload Inrun Excel File
                        </label>
                        <input 
                          type="file" 
                          accept=".xlsx, .xls"
                          onChange={(e) => handleFileSelect(e, 'inrun')}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        />
                        {selectedFiles.inrun && (
                          <p className="text-xs text-green-600 mt-1">
                            {selectedFiles.inrun.name} selected
                          </p>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Upload Outrun Excel File
                        </label>
                        <input 
                          type="file" 
                          accept=".xlsx, .xls"
                          onChange={(e) => handleFileSelect(e, 'outrun')}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        />
                        {selectedFiles.outrun && (
                          <p className="text-xs text-green-600 mt-1">
                            {selectedFiles.outrun.name} selected
                          </p>
                        )}
                      </div>
                    </div>
                  )
                ) : (
                  <p className="text-red-500 text-sm">
                    Survey type not defined for this run
                  </p>
                )}
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowUploadModal(false);
                    setSelectedFiles({ inrun: null, outrun: null });
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-100"
                >
                  Cancel
                </button>
                <button
                  onClick={handleUploadSubmit}
                  disabled={processing || !selectedFiles.inrun}
                  className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 disabled:opacity-50"
                >
                  {processing ? 'Uploading...' : 'Upload'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Navigation */}
      <div className="flex flex-wrap gap-2 border-b border-gray-200 pb-4">
        {sections.map((section) => (
          <button
            key={section.id}
            onClick={() => setActiveSection(section.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
              activeSection === section.id
                ? 'bg-red-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <span>{section.icon}</span>
            {section.label}
          </button>
        ))}
      </div>

      {/* Run Details Section */}
      {activeSection === 'details' && (
        <div className="space-y-6">
          {/* Survey Information */}
          <div className="bg-gray-50 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Survey Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">Survey From</label>
                <div className="bg-white p-3 rounded border border-gray-200 font-mono">
                  {formatValue(currentRun.survey_from, ' m')}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">Survey To</label>
                <div className="bg-white p-3 rounded border border-gray-200 font-mono">
                  {formatValue(currentRun.survey_to, ' m')}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">Survey Meterage</label>
                <div className="bg-white p-3 rounded border border-gray-200 font-mono">
                  {formatValue(currentRun.survey_meterage, ' m')}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">Survey Type</label>
                <div className="bg-white p-3 rounded border border-gray-200 font-semibold text-red-600">
                  {currentRun.survey_type_name || "-"}
                </div>
              </div>
            </div>
          </div>

          {/* Technical Details */}
          <div className="bg-gray-50 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Technical Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">Hole Section</label>
                <div className="bg-white p-3 rounded border border-gray-200">
                  {currentRun.hole_section_name || "-"}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">Survey Run In</label>
                <div className="bg-white p-3 rounded border border-gray-200">
                  {currentRun.survey_run_in_name || "-"}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">Minimum ID</label>
                <div className="bg-white p-3 rounded border border-gray-200">
                  {currentRun.minimum_id_name || "-"}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">Well Type</label>
                <div className="bg-white p-3 rounded border border-gray-200">
                  {currentRun.well_type === 1 ? 'Vertical' : currentRun.well_type === 2 ? 'Deviated/Horizontal' : '-'}
                </div>
              </div>
            </div>
          </div>

          {/* Tie-On Information */}
          {currentRun.tie_on && (
            <div className="bg-gray-50 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Tie-On Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">Measured Depth</label>
                  <div className="bg-white p-3 rounded border border-gray-200 font-mono">
                    {formatValue(currentRun.tie_on.md, ' m')}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">TVD</label>
                  <div className="bg-white p-3 rounded border border-gray-200 font-mono">
                    {formatValue(currentRun.tie_on.tvd, ' m')}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">Inclination</label>
                  <div className="bg-white p-3 rounded border border-gray-200 font-mono">
                    {formatValue(currentRun.tie_on.inc, ' °')}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">Azimuth</label>
                  <div className="bg-white p-3 rounded border border-gray-200 font-mono">
                    {formatValue(currentRun.tie_on.az, ' °')}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Upload Data Section */}
      {activeSection === 'upload' && renderUploadSection()}

      {/* Operations Section */}
      {activeSection === 'operations' && (
        <div className="space-y-6">
          <div className="bg-gray-50 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Survey Operations</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              {/* Quality Check Button */}
              <div className="text-center">
                <button
                  onClick={handleQualityCheck}
                  disabled={!uploadedFiles.inrun && !uploadedFiles.outrun}
                  className={`w-full py-3 px-4 rounded-lg font-medium text-sm ${
                    operationResults.qualityCheck
                      ? "bg-green-600 text-white hover:bg-green-700"
                      : (uploadedFiles.inrun || uploadedFiles.outrun)
                        ? "bg-blue-600 text-white hover:bg-blue-700"
                        : "bg-gray-400 text-gray-200 cursor-not-allowed"
                  }`}
                >
                  {operationResults.qualityCheck ? "View Quality Check" : "Quality Check"}
                </button>
                <p className="text-xs text-gray-500 mt-2">
                  Perform quality assessment
                </p>
              </div>

              {/* Survey Calculation Button */}
              <div className="text-center">
                <button
                  onClick={handleSurveyCalculation}
                  disabled={!uploadedFiles.inrun && !uploadedFiles.outrun}
                  className={`w-full py-3 px-4 rounded-lg font-medium text-sm ${
                    operationResults.surveyCalculation
                      ? "bg-green-600 text-white hover:bg-green-700"
                      : (uploadedFiles.inrun || uploadedFiles.outrun)
                        ? "bg-red-600 text-white hover:bg-red-700"
                        : "bg-gray-400 text-gray-200 cursor-not-allowed"
                  }`}
                >
                  {operationResults.surveyCalculation ? "View Survey Calculation" : "Survey Calculation"}
                </button>
                <p className="text-xs text-gray-500 mt-2">
                  Calculate survey trajectory
                </p>
              </div>

              {/* Comparison Button */}
              <div className="text-center">
                <button
                  onClick={handleComparison}
                  disabled={!uploadedFiles.inrun && !uploadedFiles.outrun}
                  className={`w-full py-3 px-4 rounded-lg font-medium text-sm ${
                    operationResults.comparison
                      ? "bg-green-600 text-white hover:bg-green-700"
                      : (uploadedFiles.inrun || uploadedFiles.outrun)
                        ? "bg-purple-600 text-white hover:bg-purple-700"
                        : "bg-gray-400 text-gray-200 cursor-not-allowed"
                  }`}
                >
                  {operationResults.comparison ? "View Comparison" : "Comparison"}
                </button>
                <p className="text-xs text-gray-500 mt-2">
                  Compare with previous data
                </p>
              </div>
            </div>

            {/* Status Indicators */}
            <div className="bg-white p-4 rounded-lg border border-gray-200">
              <h4 className="font-medium text-gray-700 text-sm mb-3">Calculation Status</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
                <div className={`flex items-center ${operationResults.qualityCheck ? 'text-green-600' : 'text-gray-400'}`}>
                  <div className={`w-2 h-2 rounded-full mr-2 ${operationResults.qualityCheck ? 'bg-green-600' : 'bg-gray-400'}`}></div>
                  Quality Check {operationResults.qualityCheck ? '✓' : 'Pending'}
                </div>
                <div className={`flex items-center ${(uploadedFiles.inrun || uploadedFiles.outrun) ? 'text-green-600' : 'text-gray-400'}`}>
                  <div className={`w-2 h-2 rounded-full mr-2 ${(uploadedFiles.inrun || uploadedFiles.outrun) ? 'bg-green-600' : 'bg-gray-400'}`}></div>
                  Data Uploaded {(uploadedFiles.inrun || uploadedFiles.outrun) ? '✓' : 'Pending'}
                </div>
                <div className={`flex items-center ${operationResults.comparison ? 'text-green-600' : 'text-gray-400'}`}>
                  <div className={`w-2 h-2 rounded-full mr-2 ${operationResults.comparison ? 'bg-green-600' : 'bg-gray-400'}`}></div>
                  Comparison {operationResults.comparison ? '✓' : 'Pending'}
                </div>
              </div>
            </div>

            {/* Operation Results */}
            {activeOperation === 'qualityCheck' && renderQualityCheckResults()}
            {activeOperation === 'surveyCalculation' && renderSurveyCalculationResults()}
            {activeOperation === 'comparison' && renderComparisonResults()}
          </div>
        </div>
      )}

      {/* Interpolation Prompt Modal */}
      {showInterpolationPrompt && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg max-w-sm w-full">
            {interpolationStep === 'choice' && (
              <>
                <h2 className="text-lg font-bold mb-4 text-red-600">Interpolate Survey?</h2>
                <p className="mb-4">Do you want to interpolate the survey?</p>
                <div className="flex justify-end space-x-2">
                  <button
                    className="px-4 py-2 rounded bg-gray-300 hover:bg-gray-400"
                    onClick={() => setShowInterpolationPrompt(false)}
                  >
                    Cancel
                  </button>
                  <button
                    className="px-4 py-2 rounded bg-green-600 text-white hover:bg-green-700"
                    onClick={handleInterpolationYes}
                    disabled={processing}
                  >
                    Yes
                  </button>
                  <button
                    className="px-4 py-2 rounded bg-red-600 text-white hover:bg-red-700"
                    onClick={handleInterpolationNo}
                    disabled={processing}
                  >
                    {processing ? 'Processing...' : 'No'}
                  </button>
                </div>
              </>
            )}

            {interpolationStep === 'inputs' && (
              <>
                <h2 className="text-lg font-bold mb-4 text-red-600">Interpolation Parameters</h2>
                <div className="space-y-4">
                  <div>
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={interpolationParams.fullSurvey}
                        onChange={(e) => setInterpolationParams({
                          ...interpolationParams,
                          fullSurvey: e.target.checked
                        })}
                        className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
                      />
                      <span className="text-gray-700">Interpolate Full Survey</span>
                    </label>
                  </div>

                  <input
                    type="number"
                    className="w-full px-3 py-2 border rounded"
                    placeholder="Resolution *"
                    value={interpolationParams.resolution}
                    onChange={(e) => setInterpolationParams({
                      ...interpolationParams,
                      resolution: e.target.value
                    })}
                    required
                  />

                  {!interpolationParams.fullSurvey && (
                    <>
                      <input
                        type="number"
                        className="w-full px-3 py-2 border rounded"
                        placeholder="From Depth *"
                        value={interpolationParams.fromDepth}
                        onChange={(e) => setInterpolationParams({
                          ...interpolationParams,
                          fromDepth: e.target.value
                        })}
                        required
                      />
                      <input
                        type="number"
                        className="w-full px-3 py-2 border rounded"
                        placeholder="To Depth *"
                        value={interpolationParams.toDepth}
                        onChange={(e) => setInterpolationParams({
                          ...interpolationParams,
                          toDepth: e.target.value
                        })}
                        required
                      />
                    </>
                  )}
                </div>

                <div className="flex justify-end space-x-2 mt-6">
                  <button
                    className="px-4 py-2 rounded bg-gray-300 hover:bg-gray-400"
                    onClick={() => setShowInterpolationPrompt(false)}
                    disabled={processing}
                  >
                    Cancel
                  </button>
                  <button
                    className="px-4 py-2 rounded bg-green-600 text-white hover:bg-green-700 disabled:opacity-50"
                    onClick={handleInterpolationSubmit}
                    disabled={processing}
                  >
                    {processing ? 'Processing...' : 'Submit'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}